import { useState, useCallback } from 'react'
import { AuthContext } from './context/authContextObject'
import { readAuthToken, writeAuthToken, clearAuthToken } from './lib/authToken'
import { tokenHasActivationPermission } from './lib/permissions'

const AUTH_URL = import.meta.env.VITE_AUTH_URL || 'https://auth.dev.vammo.com'

function getInitialTokenFromUrlOrSession() {
  const urlParams = new URLSearchParams(window.location.search)
  const urlToken = urlParams.get('token')

  if (urlToken) {
    const normalizedToken = writeAuthToken(urlToken)
    window.history.replaceState({}, document.title, window.location.pathname)
    return normalizedToken
  }

  return readAuthToken()
}

function decodeToken(tokenToDecode) {
  if (typeof window === 'undefined' || !window.atob || !tokenToDecode) return null

  try {
    const base64 = tokenToDecode
      .split('.')[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/')
    const padding = (4 - (base64.length % 4)) % 4
    // atob yields a Latin-1 binary string; decode the bytes as UTF-8 so
    // multibyte claims (e.g. accented names) don't corrupt the payload.
    const binary = atob(base64 + '='.repeat(padding))
    const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0))
    return JSON.parse(new TextDecoder().decode(bytes))
  } catch (error) {
    console.error('Failed to decode token:', error)
    return null
  }
}

export function AuthProvider({ children }) {
  const [token, setTokenState] = useState(getInitialTokenFromUrlOrSession)
  const [hasAccess, setHasAccess] = useState(false)
  // null | 'permission' (logged in but missing BO_BATTERY_WRITE) | 'session' (expired/invalid token)
  const [accessIssue, setAccessIssueState] = useState(null)
  const authInitialized = true

  const setToken = useCallback((nextToken) => {
    const normalizedToken = writeAuthToken(nextToken)
    setTokenState(normalizedToken)
  }, [])

  const markHasAccess = useCallback(() => {
    setAccessIssueState(null)
    setHasAccess(true)
  }, [])

  const markNoAccess = useCallback(() => {
    clearAuthToken()
    setTokenState(null)
    setHasAccess(false)
  }, [])

  // Show the guided auth screen without dropping the token (so we can tell the
  // operator exactly what's wrong before they choose to re-login).
  const setAccessIssue = useCallback((kind) => {
    setHasAccess(false)
    setAccessIssueState(kind)
  }, [])

  const redirectToLogin = useCallback(() => {
    const redirectUrl = `https://${window.location.hostname}${window.location.pathname}`
    window.location.href = `${AUTH_URL}?redirectUrl=${encodeURIComponent(redirectUrl)}`
  }, [])

  // "Entrar novamente": drop the current (unauthorized/expired) token and
  // re-authenticate. On return, the boot flow re-checks the permission.
  const relogin = useCallback(() => {
    clearAuthToken()
    setTokenState(null)
    setHasAccess(false)
    setAccessIssueState(null)
    redirectToLogin()
  }, [redirectToLogin])

  // Returns true | false | null (null = claim missing/malformed → fail open).
  const hasActivationPermission = useCallback(
    (candidateToken) => tokenHasActivationPermission(decodeToken(candidateToken ?? token)),
    [token],
  )

  const value = {
    token,
    hasAccess,
    accessIssue,
    authInitialized,
    setToken,
    markHasAccess,
    markNoAccess,
    setAccessIssue,
    redirectToLogin,
    relogin,
    decodeToken,
    hasActivationPermission,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
